// Curated placeholder imagery — one consistent visual register across the
// site: dusk-lit architecture, open land, coast. All free Unsplash sources,
// hotlinked with width/quality params. Every slot is designed to be replaced
// by client photography later: swap the URL in /admin or here.

const u = (id, w = 1800) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=80`;

export const IMG = {
  // Page heroes — self-hosted (see /public/img). These used to be hotlinked
  // to images.unsplash.com like the rest of this file; on some networks
  // (ad-blockers, corporate/mobile carrier filtering, or just intermittent
  // Unsplash slowness) that request stalls or gets blocked, and BrandImage's
  // onError permanently falls back to the "Coming soon" placeholder for that
  // page load — so visitors saw an empty-looking hero instead of the photo.
  // Same failure class already fixed once for the Earth texture (see
  // scene/Earth.jsx) by moving off a third-party host onto same-origin.
  aboutHero:    "/img/about-hero.jpg",     // modern villa at dusk
  projectsHero: "/img/projects-hero.jpg",  // luxury residence, pool, dusk
  contactHero:  "/img/contact-hero.jpg",   // architectural facade

  // About page
  story:  u("1500382017468-9049fed747ef"),        // open land, golden hour
  vision: u("1518780664697-55e3ad937233", 2000),   // misty green hills — distinct from story/cta
  cta:    u("1505852679233-d9fd70aff56d", 2000),   // villa pool at night

  // Team portrait placeholders — neutral professional headshots until the
  // client supplies real photography (swap in /admin → About → People).
  people: [
    u("1560250097-0b93528c311a", 900),            // founder — suit, warm light
    u("1573496359142-b8d87734a5a2", 900),         // operations — professional
    u("1556157382-97eda2d62296", 900),            // legal — professional
  ],

  // Project imagery (fallbacks when a project has no photo yet)
  project: {
    "nausiddhi-residency":       u("1600596542815-ffad4c1539a9"),  // estate home
    "blue-rain-resort-homes":    u("1507525428034-b723cf961d3e"),  // coastline
    "sai-shrusti":               u("1600585154340-be6161a56a0c"),  // modern home
    "nausiddhi-coastal-greens":  u("1519046904884-53103b34b206", 2000), // palm coast — Konkan register
  },
  projectDefault: u("1600047509807-ba8f99d2cdde"),
};

// ---------------------------------------------------------------------------
// Guaranteed fallback — a branded, procedurally-varied plate (deep forest
// gradient + topographic contour lines + horizon + wordmark). Zero network
// dependency, so an image slot can NEVER render as an empty gap even if a
// remote URL 404s or Unsplash is blocked. `hue` shifts the accent so a row of
// placeholder panels reads as a *designed set* rather than identical blocks.
// ---------------------------------------------------------------------------
function makePlate(hue = 0, seed = 1) {
  // deterministic contour paths
  const rows = [];
  let s = seed * 2654435761;
  const rnd = () => { s = (s * 1103515245 + 12345) & 0x7fffffff; return (s % 1000) / 1000; };
  for (let r = 0; r < 7; r++) {
    const baseY = 220 + r * 105 + rnd() * 30;
    let d = `M0 ${baseY}`;
    for (let x = 0; x <= 1600; x += 200) {
      const y = baseY - Math.sin((x / 1600) * Math.PI * (1.5 + r * 0.4) + rnd() * 3) * (30 + r * 8);
      d += ` L${x} ${Math.round(y)}`;
    }
    rows.push(`<path d='${d}' fill='none' stroke='#d4a45c' stroke-opacity='${(0.05 + r * 0.012).toFixed(3)}' stroke-width='1'/>`);
  }
  const tint = `hsl(${(150 + hue) % 360},32%,${8 + (hue % 6)}%)`;
  const tint2 = `hsl(${(150 + hue) % 360},40%,5%)`;
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='1600' height='1000' viewBox='0 0 1600 1000'>` +
    `<defs><linearGradient id='g' x1='0' y1='0' x2='0.6' y2='1'>` +
    `<stop offset='0' stop-color='${tint}'/><stop offset='0.6' stop-color='#05140b'/>` +
    `<stop offset='1' stop-color='${tint2}'/></linearGradient>` +
    `<radialGradient id='v' cx='0.5' cy='0.42' r='0.75'>` +
    `<stop offset='0.55' stop-color='#000' stop-opacity='0'/><stop offset='1' stop-color='#000' stop-opacity='0.45'/>` +
    `</radialGradient></defs>` +
    `<rect width='1600' height='1000' fill='url(#g)'/>` +
    rows.join("") +
    `<rect width='1600' height='1000' fill='url(#v)'/>` +
    `<rect x='60' y='60' width='1480' height='880' fill='none' stroke='#d4a45c' stroke-opacity='0.16'/>` +
    `<line x1='740' y1='486' x2='860' y2='486' stroke='#d4a45c' stroke-opacity='0.6' stroke-width='1'/>` +
    `<text x='800' y='530' text-anchor='middle' font-family='Montserrat, sans-serif' ` +
    `font-size='24' letter-spacing='14' fill='#d4a45c' fill-opacity='0.8'>NAUSIDDHI</text>` +
    `<text x='800' y='566' text-anchor='middle' font-family='Montserrat, sans-serif' ` +
    `font-size='12' letter-spacing='7' fill='#f4f0e4' fill-opacity='0.38'>THE NEW STANDARD</text>` +
    `</svg>`;
  return "data:image/svg+xml," + encodeURIComponent(svg);
}

export const FALLBACK_IMG = makePlate(0, 1);
// A small palette of pre-baked variants so different slots differ visually.
const PLATE_VARIANTS = [makePlate(0, 3), makePlate(28, 7), makePlate(-16, 11), makePlate(14, 17)];
export function fallbackForKey(key = "") {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) & 0xffff;
  return PLATE_VARIANTS[h % PLATE_VARIANTS.length];
}

// onError handler for any <img>: swaps the broken src for the branded
// fallback exactly once (the guard prevents an infinite error loop).
export function onImgError(e) {
  const el = e?.currentTarget;
  if (!el || el.dataset.fallbackApplied === "1") return;
  el.dataset.fallbackApplied = "1";
  el.src = FALLBACK_IMG;
  el.style.opacity = "1";
  el.style.display = "";
}

export function projectImage(p) {
  if (p?.image) return p.image; // admin-supplied photo wins
  return IMG.project[p?.id] || IMG.projectDefault;
}
